import {
  Injectable,
  InjectionToken,
  WritableSignal,
  inject,
  signal,
} from '@angular/core';
import { createAbility } from '@kinet-open-network/shared/data-access/auth';
import { AbilityInterface } from '@kinet-open-network/shared/data-access/model';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { selectAuthenticatedUser } from '../+state/auth.selectors';

export const Ability = new InjectionToken<AbilityInterface>('Ability', {
  providedIn: 'root',
  factory: () => createAbility(),
});

export const ABILITY = new InjectionToken<WritableSignal<AbilityInterface>>(
  'Ability signal',
  {
    providedIn: 'root',
    factory: () => signal(createAbility()),
  },
);

/**
 * Helper function to test ability (alter between empty and full rules)
 *
 * @param ability
 * @param interval
 */
const testAbility = (ability: AbilityInterface, interval = 1000) => {
  let rules = ability.rules;

  setInterval(() => {
    if (!rules.length) {
      rules = ability.rules;
    }

    ability.update(ability.rules.length > 0 ? [] : rules);
  }, interval);
};

@Injectable({
  providedIn: 'root',
})
export class AbilityService {
  readonly ability = inject(ABILITY);
  readonly ability$: Observable<AbilityInterface>;
  private _ability = inject(Ability);
  private readonly store = inject(Store);
  readonly user = this.store.selectSignal(selectAuthenticatedUser);

  constructor() {
    this.ability$ = new Observable((subscriber) => {
      // set initial value to observable and signal
      subscriber.next(this._ability);
      this.ability.set(this._ability);

      return this._ability.on('updated', () => {
        // emit new value to observable
        subscriber.next(this._ability);
        // update signal - we need to create new instance of the ability to trigger change detection
        this.ability.set(createAbility(this._ability.rules));
      });
    });

    // testAbility(this._ability, 5000);
  }

  updateAbilities(rules: AbilityInterface['rules']) {
    this._ability.update(rules);
  }

  can(...args: Parameters<AbilityInterface['can']>) {
    return this._ability.can(...args);
  }

  cannot(...args: Parameters<AbilityInterface['cannot']>) {
    return this._ability.cannot(...args);
  }
}
